import React, { useState, useEffect, useMemo } from 'react';
import { getCategories, getModels } from '../services/adminStorage';
import ModelCard from '../components/ModelCard';
import { Search, ChevronLeft, ChevronRight, SlidersHorizontal, Sparkles } from 'lucide-react';

const ITEMS_PER_PAGE = 9;

export default function CatalogPage({ onSelectModel }) {
  const [categories, setCategories] = useState(getCategories());
  const [models, setModels] = useState(getModels());

  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const handleUpdate = () => {
      setCategories(getCategories());
      setModels(getModels());
    };
    window.addEventListener('suzume_data_updated', handleUpdate);
    return () => window.removeEventListener('suzume_data_updated', handleUpdate);
  }, []);

  // Filtragem de modelos por categoria e busca
  const filteredModels = useMemo(() => {
    return models.filter((model) => {
      const matchCategory = 
        selectedCategory === 'todos' || model.categorySlug === selectedCategory;
      
      const query = searchQuery.toLowerCase().trim();
      const matchQuery = 
        !query ||
        model.name.toLowerCase().includes(query) ||
        model.category.toLowerCase().includes(query) ||
        model.description.toLowerCase().includes(query) ||
        (model.tags && model.tags.some(tag => tag.toLowerCase().includes(query)));

      return matchCategory && matchQuery;
    });
  }, [models, selectedCategory, searchQuery]);

  // Reset da página quando os filtros mudam
  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Cálculo da Paginação (Até 9 itens por página)
  const totalPages = Math.ceil(filteredModels.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedModels = filteredModels.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 250, behavior: 'smooth' });
    }
  };

  return (
    <div className="section-padding" style={{ minHeight: '80vh', backgroundColor: '#ffffff' }}>
      <div className="container">
        
        {/* Banner do Catálogo */}
        <div className="text-center" style={{ maxWidth: '750px', margin: '0 auto 2.5rem' }}>
          <div className="badge badge-brand" style={{ marginBottom: '0.75rem' }}>
            <Sparkles size={14} />
            Acervo Completo Suzume
          </div>
          <h1 style={{ fontSize: '2.5rem', color: 'var(--text-title)', marginBottom: '0.75rem' }}>
            Catálogo de Landing Pages
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '1.05rem' }}>
            Explore nossos modelos profissionais sob assinatura por nichos de mercado. Selecione o modelo perfeito e solicite seu orçamento via WhatsApp.
          </p>
        </div>

        {/* Barra de Filtros e Busca */}
        <div className="catalog-filter-bar">
          <div style={{ position: 'relative', width: '100%', maxWidth: '540px', margin: '0 auto' }}>
            <Search 
              size={20} 
              color="var(--text-muted)" 
              style={{ position: 'absolute', left: '1.25rem', top: '50%', transform: 'translateY(-50%)' }} 
            />
            <input 
              type="text"
              placeholder="Buscar por nome, nicho (ex: artesanato, mecânica, saúde)..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="catalog-search-input"
            />
          </div>

          {/* Categorias (Pills) */}
          <div style={{ margin: '1rem 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-muted)' }}>
              <SlidersHorizontal size={16} />
              <span>Filtrar por Categoria:</span>
            </div>
            <div className="category-pills">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`category-pill ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(cat.id)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Contador de Resultados */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)', paddingBottom: '1rem' }}>
            <span>
              Exibindo <strong>{filteredModels.length > 0 ? startIndex + 1 : 0}–{Math.min(startIndex + ITEMS_PER_PAGE, filteredModels.length)}</strong> de <strong>{filteredModels.length}</strong> modelos encontrados
            </span>
            <span>Página {currentPage} de {totalPages}</span>
          </div>
        </div>

        {/* Grid de Cards do Catálogo (até 9 por página) */}
        {paginatedModels.length > 0 ? (
          <div className="cards-grid">
            {paginatedModels.map((model) => (
              <ModelCard 
                key={model.id} 
                model={model} 
                onSelectModel={onSelectModel} 
              />
            ))}
          </div>
        ) : (
          <div style={{ textOverflow: 'ellipsis', padding: '4rem 2rem', textAlign: 'center', backgroundColor: 'var(--bg-main)', borderRadius: '16px', border: '1px dashed var(--border-color)' }}>
            <h3 style={{ fontSize: '1.25rem', color: 'var(--text-title)', marginBottom: '0.5rem' }}>Nenhum modelo encontrado</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Tente buscar com outros termos ou selecione "Todos os Nichos".</p>
            <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedCategory('todos'); setSearchQuery(''); }}>
              Limpar Filtros
            </button>
          </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <button 
              className="page-btn" 
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Página anterior"
            >
              <ChevronLeft size={20} />
            </button>

            {[...Array(totalPages)].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => goToPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button 
              className="page-btn" 
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Próxima página"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
